import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, takeUntil, map, filter, distinctUntilChanged } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { EqualityValidationService, EqualityValidationError } from '../../../services/mbrs/equality-validation.service';
import { EquityReconciliationService } from '../../../services/mbrs/equity-reconciliation.service';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import { FormSchemaService } from '../../../services/mbrs/form-schema.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import {
  FORM_CALCULATION_DEBOUNCE_MS,
  STATE_INHERITANCE_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { isRowVisible, isCollapsibleHeader as isCollapsibleHeaderRule, headerLevelClass } from '../../../utils/section-visibility.util';
import { SubmitForValidationComponent } from '../../common/submit-for-validation/submit-for-validation.component';
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import {
  pickRole8SchemaFile,
} from '../../../services/mbrs/validators/role8-statement-of-financial-position.rules';
import { isCellVisible } from '../../../services/mbrs/validators/dimension-visibility.util';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';
import { isMandatoryForValidation } from '../../../utils/required-field.util';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import {
  referencedSheetKeysFromSchema,
  recomputeReferencedSheets,
  buildSocieColumnContext,
} from '../../../services/mbrs/cross-sheet-recompute.util';
import { enableDualColumnRowAndRevealVisible } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

interface SchemaControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    level?: string | number;
    note?: string;
    rows?: number;
    placeholder?: string;
    readonly?: boolean;
    calculated?: boolean;
    dualColumn?: boolean;
    linkTo?: {
      // Structured link to source role - fields with linkTo inherit values from sub-sheets
      // These fields should always show "Validated" status and no warning background
      role: string; // Role code (e.g., "210100")
      qname: string; // Target qname in source role
    };
    inheritFromRole9?: boolean; // LEGACY: Specific for Role-9 inheritance
    inheritFromRole?: string; // LEGACY: Generic: e.g., "Role9[210100]"
  };
  required?: boolean;
  validators?: Array<{ name: string; value?: number }>;
}

interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: SchemaControl[];
}

@Component({
  selector: 'app-role8-210000-statement-of-financial-position',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    SkeletonModule,
    FieldLinkTargetDirective,
  ],
  providers: [MessageService],
  templateUrl: './role8-210000-statement-of-financial-position.component.html',
  styleUrls: ['./role8-210000-statement-of-financial-position.component.css'],
})
export class Role8210000StatementOfFinancialPositionComponent
  implements OnInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private equalityValidation = inject(EqualityValidationService);
  private equityReconciliation = inject(EquityReconciliationService);
  private validationContext = inject(ValidationContextService);
  private formSchemaService = inject(FormSchemaService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private envService = inject(EnvironmentService);
  private cdr = inject(ChangeDetectorRef);
  private mappingStatus = inject(MappingStatusService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);

  form!: FormGroup;
  schema!: FormSchema;
  isLoading = true;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Financial Position (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfFinancialPosition
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  // Flag to track if we're loading saved state
  private isLoadingState = false;
  private isCalculating = false;
  private calculatedTotalKeys: string[] = [];
  isValidationComplete = false;

  // Track which Level 4 sections are expanded (all expanded by default)
  expandedSections = new Set<string>(); // Track which sections are expanded (Golden Standard pattern)

  /** Bound as a property so the directive gets a stable, `this`-correct function. */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);

  // Edit mode tracking per row (key: qname, value: original values)
  private editingRows = new Map<string, { [key: string]: number | null }>();

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes. */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  private validationErrors = new Map<string, string[]>();
  private mandatoryFieldErrors = new Set<string>(); // Track qnames with mandatory field errors (for red/Failed status)
  private equalityValidationErrors = new Map<string, EqualityValidationError[]>(); // Track equality validation errors
  private equalityValidationGeneration = 0; // Prevents stale async callbacks from overwriting newer results

  /**
   * Qnames whose entries in `validationErrors` were contributed by the LAST
   * cross-sheet run (`runEqualityValidations` merges equality error messages
   * into the shared `validationErrors` map). The same-sheet live pass must NOT
   * wipe these — it deletes only keys absent from this set, so the frozen
   * cross-sheet verdict survives until the next Tick re-runs the cross-sheet
   * pass. Rebuilt after every cross-sheet run from `equalityValidationErrors`.
   */
  private crossSheetErrorQnames = new Set<string>();

  /**
   * Get currency symbol with unit from Role-1
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get rounding format from Role-1
   * e.g., "Actuals", "000", "000000", "000000000"
   */
  get roundingUnit(): string {
    return this.crossRefService.getCurrencyWithRounding();
  }

  /**
   * Get formatted date range for current year
   * e.g., "01/01/2024 - 31/12/2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /**
   * Get formatted date range for previous year
   * e.g., "01/01/2023 - 31/12/2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  /**
   * The four (dimension × period) visibility cells for this sheet, derived by
   * the shared, config-driven resolver via CrossReferenceService.getVisibility().
   * The SAME resolver + central registry feeds the pure
   * role8StatementOfFinancialPositionValidator, so the mounted editor and the
   * headless listing cannot drift on which columns are shown/validated.
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.statementOfFinancialPosition);
  }

  /**
   * Check if Consolidated Current column should be displayed
   * Rules: Show for Consolidated (both 1st time and Subsequent)
   */
  get showConsolidatedCurrent(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Consolidated', 'CurrentYear');
  }

  /**
   * Check if Consolidated Previous column should be displayed
   * Rules: Show only for Consolidated + Subsequent
   */
  get showConsolidatedPrevious(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Consolidated', 'PreviousYear');
  }

  /**
   * Check if Separate Current column should be displayed
   * Rules: Always show (for both Separate and Consolidated)
   */
  get showSeparateCurrent(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Separate', 'CurrentYear');
  }

  /**
   * Check if Separate Previous column should be displayed
   * Rules: Show for Separate + Subsequent, or Consolidated + Subsequent
   */
  get showSeparatePrevious(): boolean {
    return isCellVisible(this.dimensionVisibility, 'Separate', 'PreviousYear');
  }

  /**
   * Get total number of data columns to display
   * Used for colspan calculations in headers and section rows
   */
  get totalDataColumns(): number {
    let count = 0;
    if (this.showConsolidatedCurrent) count++;
    if (this.showConsolidatedPrevious) count++;
    if (this.showSeparateCurrent) count++;
    if (this.showSeparatePrevious) count++;
    return count;
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '8',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    try {
      await this.loadSchema();
      this.initializeExpandedSections();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.setupEditPipeline();
      this.updateStatementSheetRowStatusInStateService(true); // Update initial edited status, skip sheet-level (validation not run yet)

      // Hydration window: the synchronous portion of loadSavedState + the
      // initial (STEP 1) Role-9 inheritance pull both perform direct
      // updateSheetData() writes that are NOT user-driven. Bracket them so
      // notifyCrossTab is suppressed for the duration. The deferred
      // setTimeout(...) inside loadSavedState extends past this window — the
      // service's withHydration() short-circuits notifyCrossTab via a flag,
      // so we re-bracket those writes individually inside loadSavedState.
      // setupRole9Inheritance's STEP 2 subscription has its own guard via
      // the `state$` BehaviorSubject replay (see below).
      this.stateService.beginHydration();
      try {
        this.loadSavedState();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
        this.setupRole9Inheritance(); // ADDED: Subscribe to Role-9 state changes
      } finally {
        this.stateService.endHydration();
      }
      this.setupRemoteUpdateRevalidation();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load the form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-1
   */
  private async loadSchema(): Promise<void> {
    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role8');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard.
    // Filename literals come from
    // role8-statement-of-financial-position.rules.ts so the editor and the
    // pure validator derive the schema selection from the SAME source —
    // the parity-check harness will surface any drift.
    // Delegate to the shared (MFRS-default) `pickRole8SchemaFile()` and pass
    // the RAW accounting standard so the editor and the pure validator
    // resolve the SAME file. Collapsing to an isMFRS boolean here would
    // diverge from the validator for an unknown/empty standard (the picker
    // selects MPERS only on the exact MPERS literal).
    const accountingStandard =
      this.crossRefService.getCrossReferenceData().accountingStandard;
    const schemaFile = pickRole8SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      `/assets/forms/${schemaFile}`,
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

    // Preload schemas for sheets referenced by equality validation rules
    // so that error messages show field labels instead of raw qnames
    const isMFRSForPreload = this.crossRefService.isMFRS();
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRSForPreload ? 'MFRS' : 'MPERS';
    await this.validationContext.preloadReferencedSchemas(this.schema, currentTaxonomy);
  }

  /**
   * Build the reactive form with XBRL dimensional naming
   * Format: {qname}[Consolidated|Separate][CurrentYear|PreviousYear]
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }

    const formControls: { [key: string]: FormControl } = {};
    const prefix = this.getXbrlPrefix(); // Get dynamic prefix based on schema

    this.schema.controls.forEach((control: SchemaControl) => {
      // Skip section headers
      if (control.type === 'SectionHeader') {
        return;
      }

      const validators: any[] = this.getValidators(control);
      const defaultValue = null;

      // For MonetaryItemType controls, create 4 XBRL dimensional controls
      if (control.type === 'MonetaryItemType' && control.ui.dualColumn) {
        // All fields start disabled (edit mode will enable them)
        // Calculated and readonly fields remain permanently disabled
        const startsDisabled = true;

        // Consolidated - Current Year
        formControls[
          `${control.qname}[Consolidated][CurrentYear]`
        ] = new FormControl(
          { value: defaultValue, disabled: startsDisabled },
          validators
        );

        // Consolidated - Previous Year
        formControls[
          `${control.qname}[Consolidated][PreviousYear]`
        ] = new FormControl(
          { value: defaultValue, disabled: startsDisabled },
          validators
        );

        // Separate - Current Year
        formControls[
          `${control.qname}[Separate][CurrentYear]`
        ] = new FormControl(
          { value: defaultValue, disabled: startsDisabled },
          validators
        );

        // Separate - Previous Year
        formControls[
          `${control.qname}[Separate][PreviousYear]`
        ] = new FormControl(
          { value: defaultValue, disabled: startsDisabled },
          validators
        );
      } else {
        // TextBlock and other non-monetary controls (though Role-8 only has section headers and monetary)
        formControls[control.qname] = new FormControl(defaultValue, validators);
      }
    });

    this.form = new FormGroup(formControls);
  }

  /**
   * Get validators for a control
   */
  private getValidators(control: SchemaControl): any[] {
    const validators: any[] = [];

    if (control.required) {
      // Note: We don't add Validators.required for now since we're checking all 4 columns together
    }

    const minValidator = control.validators?.find((validator) => validator.name === 'min');
    if (minValidator && minValidator.value !== undefined) {
      // Note: Min validator would need to be applied per column
    }

    return validators;
  }

  /**
   * Detect XBRL prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
   * Checks schema qnames to determine which taxonomy is being used
   */
  private getXbrlPrefix(): string {
    if (!this.schema || !this.schema.controls) {
      return 'ifrs-full'; // Default to MFRS
    }

    // Check if any control qname uses ifrs-smes (MPERS)
    const hasMpers = this.schema.controls.some(
      (control) =>
        control.qname.startsWith('ifrs-smes:') ||
        control.qname.includes('mpers')
    );

    if (hasMpers) {
      return 'ifrs-smes';
    }

    // Check if any control qname uses ifrs-full (MFRS)
    const hasMfrs = this.schema.controls.some(
      (control) =>
        control.qname.startsWith('ifrs-full:') || control.qname.includes('mfrs')
    );

    if (hasMfrs) {
      return 'ifrs-full';
    }

    // Default to MFRS (ifrs-full)
    return 'ifrs-full';
  }

  /**
   * Get XBRL control name
   * Supports both MFRS (ifrs-full) and MPERS (ifrs-smes) taxonomies
   */
  getXbrlControlName(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  }

  /**
   * Wire the LIVE (in-edit) feedback cycle: calc → same-sheet validate →
   * cross-sheet validate (display-only) → status.
   *
   * Defer-to-Tick: while a row is mid-edit the live pipeline does NOT persist
   * (persist stays in the Tick handler, saveEditMode). Cross-sheet validation
   * (`validateCrossSheet`) IS wired here so role9 equality errors surface inline
   * immediately — but it never persists state; it only updates display.
   * This makes "no commit until Tick" structural rather than flag-gated
   * (see buildEditModeLivePipeline).
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.form.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState || this.isCalculating,
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => this.validateSameSheet(),
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Run all validations and trigger a single change detection at the end.
   * This prevents the flicker caused by intermediate detectChanges calls that
   * temporarily render the template without errors from later validation methods.
   *
   * Async because runEqualityValidations() performs a cross-sheet schema load
   * (role-610000 SOCIE) followed by validateCrossSheetEquityByYear. Callers
   * that need sheet-level status to reflect cross-sheet errors MUST await.
   */
  private async runAllValidations(): Promise<void> {
    this.validationErrors.clear();
    this.mandatoryFieldErrors.clear();
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    this.validateAgainstApiData();
    await this.runEqualityValidations();

    // runEqualityValidations re-populated equalityValidationErrors; record which
    // qnames it merged into validationErrors so a later same-sheet-only pass
    // (validateSameSheet) leaves the cross-sheet entries frozen.
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.forEach((_unusedErrors, qname) => this.crossSheetErrorQnames.add(qname));

    // Single change detection after ALL validations complete
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * SAME-SHEET-ONLY validation pass (the live, in-edit feedback).
   *
   * Runs while a row is mid-edit: re-derives this sheet's own
   * mandatory-field + extracted-sum-mismatch entries WITHOUT touching the
   * cross-sheet (equality / SOCIE) verdict. The frozen cross-sheet entries —
   * recorded in `crossSheetErrorQnames` by the last cross-sheet run — are
   * preserved verbatim: we delete only `validationErrors` keys that are NOT in
   * that set, then re-run the same-sheet validators (which re-add same-sheet
   * entries only). Cross-sheet re-validation happens on Tick (validateCrossSheet).
   */
  private validateSameSheet(): void {
    // Clear ONLY same-sheet entries from validationErrors — leave the frozen
    // cross-sheet entries in place until the next Tick re-runs them.
    const keysToDelete: string[] = [];
    this.validationErrors.forEach((_unusedMessages, key) => {
      if (!this.crossSheetErrorQnames.has(key)) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => this.validationErrors.delete(key));

    // validateAgainstApiData internally clears mandatoryFieldErrors + re-runs
    // validateMandatoryFields, then merges the same-sheet extracted-sum
    // warnings — none of which touch the cross-sheet equality verdict.
    this.mandatoryFieldErrors.clear();
    this.validateAgainstApiData();

    // Same-sheet equality (Assets == Total equity and liabilities) is a self-
    // balancing check whose operands both live on THIS sheet — so it is safe and
    // expected to fire live, like the mandatory/extracted-sum checks above. It
    // used to ride only on runEqualityValidations (Tick-only, validateCrossSheet),
    // so the deferred-commit rework (4f1e972) left the balance error invisible
    // until the user clicked the Tick. Run it here so the red border/message
    // appear the moment the user edits. Cross-sheet equality (CashAndBankBalances
    // ↔ role18, SOCIE equity) stays deferred to Tick.
    this.validateSameSheetEquality();

    // Single change detection after the same-sheet pass completes.
    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * Qnames of THIS sheet's same-sheet equality controls — i.e. controls whose
   * every `validation.equality` rule resolves back to this sheet (the
   * self-balancing Assets == EquityAndLiabilities pair). Cross-sheet equality
   * controls (CashAndBankBalances → role18) are intentionally excluded so the
   * live pass never touches the Tick-managed cross-sheet partition.
   */
  private getSameSheetEqualityQnames(): Set<string> {
    const own = SHEET_KEYS.statementOfFinancialPosition;
    const set = new Set<string>();
    for (const control of this.schema?.controls ?? []) {
      const eq = (control as any).validation?.equality as Array<{ sheet?: string }> | undefined;
      if (
        eq &&
        eq.length > 0 &&
        eq.every((rule) => (this.validationContext.resolveSheetKey(rule.sheet ?? own) ?? rule.sheet ?? own) === own)
      ) {
        set.add(control.qname);
      }
    }
    return set;
  }

  /**
   * LIVE same-sheet equality pass (Assets == Total equity and liabilities).
   *
   * Mirrors the SAME-SHEET portion of runEqualityValidations: an own-sheet
   * recompute (so Assets/EquityAndLiabilities are baseline-anchored exactly as
   * the pure validator computes them) followed by validateAllEqualityRules.
   * Only the same-sheet qnames are written into `equalityValidationErrors`
   * (border/status/message source) and `validationErrors` (preserving any
   * extracted-sum warning) — added on mismatch, cleared on balance. Cross-sheet
   * qnames are left untouched for the next Tick (validateCrossSheet) to manage.
   * The published green/red verdict is unaffected (it comes from the pure
   * validator, which already includes this check); this only brings the editor
   * display into live agreement with that verdict.
   */
  private validateSameSheetEquality(): void {
    if (!this.schema) return;

    const sameSheetQnames = this.getSameSheetEqualityQnames();
    if (sameSheetQnames.size === 0) return;

    const isMFRS = this.schema.controls.some(
      (control) => control.qname === 'ifrs-full:Assets' || control.qname === 'ifrs-full:EquityAndLiabilities'
    );
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Own-sheet recompute only — no cross-sheet read, so the live path stays
    // cheap (matches the own-sheet baseline-anchoring in runEqualityValidations).
    const bag = recomputeReferencedSheets(
      { [SHEET_KEYS.statementOfFinancialPosition]: this.form.getRawValue() },
      [SHEET_KEYS.statementOfFinancialPosition],
      currentTaxonomy,
      () => this.schema,
    );
    const results = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      bag,
      currentTaxonomy,
      SHEET_KEYS.statementOfFinancialPosition,
    );

    sameSheetQnames.forEach((qname) => {
      const errs = results[qname];
      const preserved = (this.validationErrors.get(qname) || []).filter((message) => message.includes(EXTRACTED_SUM_MARKER));
      if (errs && errs.length > 0) {
        this.equalityValidationErrors.set(qname, errs);
        this.validationErrors.set(qname, [...preserved, ...errs.map((equalityError) => equalityError.message)]);
      } else {
        this.equalityValidationErrors.delete(qname);
        if (preserved.length > 0) {
          this.validationErrors.set(qname, preserved);
        } else {
          this.validationErrors.delete(qname);
        }
      }
    });
  }

  /**
   * CROSS-SHEET validation pass (commit-only — runs on Tick, never live).
   *
   * Re-runs the async equality + SOCIE equity reconciliation. Because
   * `runEqualityValidations` merges its error MESSAGES into the shared
   * `validationErrors` map (not just `equalityValidationErrors`), we first strip
   * the previous cross-sheet contributions from `validationErrors` (preserving
   * any same-sheet "does not match the extracted sum" warning on the same
   * qname), reset the tracking set + `equalityValidationErrors`, re-run, then
   * rebuild `crossSheetErrorQnames` from the fresh `equalityValidationErrors`.
   */
  private async validateCrossSheet(): Promise<void> {
    // Strip previous cross-sheet contributions from the shared map. A qname may
    // also carry a same-sheet suggested-change warning ("does not match the
    // extracted sum") — keep that, drop only the equality/cross-sheet messages.
    this.crossSheetErrorQnames.forEach((qname) => {
      const errors = this.validationErrors.get(qname);
      if (!errors) return;
      const sameSheetOnly = errors.filter((message) => message.includes(EXTRACTED_SUM_MARKER));
      if (sameSheetOnly.length > 0) {
        this.validationErrors.set(qname, sameSheetOnly);
      } else {
        this.validationErrors.delete(qname);
      }
    });
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    await this.runEqualityValidations();

    // runEqualityValidations re-merged the fresh equality messages into
    // validationErrors and re-populated equalityValidationErrors — record those
    // qnames so the next same-sheet pass leaves them frozen.
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.forEach((_unusedErrors, qname) => this.crossSheetErrorQnames.add(qname));

    this.validationErrors = new Map(this.validationErrors);
    this.cdr.detectChanges();
  }

  /**
   * Subscribe to Role-9 (Sub-classification) state changes and pull values into linked fields
   */
  private setupRole9Inheritance(): void {
    // STEP 1: Load existing Role-9 data immediately
    const initialRole9Data = this.stateService.getSheetData(
      SHEET_KEYS.subClassificationOfAssetsLiabilitiesEquity
    );

    if (initialRole9Data && Object.keys(initialRole9Data).length > 0) {
      this.pullRole9Values(initialRole9Data);

      const fullFormData = this.form.getRawValue();
      // Use skipEditedMark: true during initialization to prevent marking as edited
      this.stateService.updateSheetData(
        SHEET_KEYS.statementOfFinancialPosition,
        fullFormData,
        this.form.valid,
        true // skipEditedMark
      );
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      //console.log('[Role9Inheritance] State service synced after initial inheritance');
    }

    // STEP 2: Subscribe to future Role-9 data changes
    //
    // Two-layer guard against spurious init-time firings:
    //   1. distinctUntilChanged on a Role-9 hash — the BehaviorSubject's replayed
    //      first emission carries the SAME role9 slice we already pulled in STEP 1
    //      (or an empty slice). distinctUntilChanged drops it once the second
    //      emission carries identical content, but the FIRST replayed emission
    //      always passes through (no prior value to compare against). To handle
    //      that, we also use:
    //   2. filter(!isSheetInitializing) — drops emissions that fire while Role-8
    //      is still mid-init. By the time markSheetInitialized() runs (~700ms
    //      after loadSavedState completes), the BehaviorSubject's first replay
    //      has already passed through this filter and been dropped.
    //
    // Net effect: the [notify-entry] storm from this subscription's mount-time
    // replay is suppressed; only genuine Role-9 changes that occur after Role-8
    // is initialized propagate through.
    this.stateService.state$
      .pipe(
        map((state) => state.subClassificationOfAssetsLiabilitiesEquity),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfFinancialPosition)),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        debounceTime(STATE_INHERITANCE_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(async (role9Data) => {
        if (!role9Data || Object.keys(role9Data).length === 0) {
          return;
        }

        // A Role-9 change must refresh Role-8 COMPLETELY, not just the inherited
        // leaf. pullRole9Values() overwrites the Role-9-linked leaves with
        // emitEvent:false, so it never trips Role-8's own valueChanges edit
        // pipeline (calc → persist → validate → status). Without the explicit
        // steps below, a Role-9 edit updated Role-8's inherited line (e.g.
        // "Total non-current borrowings") but left Role-8's calculated totals
        // (e.g. "Total non-current liabilities" → Liabilities → Equity &
        // Liabilities), its Assets == Equity & Liabilities balance check, its
        // red borders / N8N-baseline "Success *" warnings, AND its sheet-level
        // status all frozen at the pre-change value — only refreshing when the
        // user manually edited a Role-8 field. We therefore replay the same
        // ordered cycle the edit pipeline runs.
        this.pullRole9Values(role9Data);

        // 1. Recompute Role-8's calculated totals from the freshly-inherited
        //    leaves (emitEvent:false inside; no re-entrant valueChanges).
        this.calculateTotals();

        // 2. Persist — ALWAYS skip the edited mark: inheriting from Role-9 is
        //    automatic data flow, not a user edit.
        const fullFormData = this.form.getRawValue();
        this.stateService.updateSheetData(
          SHEET_KEYS.statementOfFinancialPosition,
          fullFormData,
          this.form.valid,
          true // Always skip edited mark for Role-9 inheritance
        );

        // 3. Re-run validations (awaited — runEqualityValidations performs the
        //    async SOCIE cross-sheet equity load) so red borders, API-mismatch
        //    warnings, and the equality verdict reflect the new totals.
        await this.runAllValidations();

        // 4. Re-publish the sheet-level status. For this pure-verdict sheet,
        //    updateSheetLevelStatus re-runs the Role-8 validator against Role-9's
        //    current data, so the cross-match error and the N8N "Success *"
        //    asterisk are raised/cleared in lock-step with the Role-9 edit
        //    instead of lagging a cycle behind it.
        this.evaluateSheetStatus();
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        //console.log('[Role9Inheritance] State service synced after subscription inheritance');
      });
  }

  /**
   * Pull values from Role-9 data into Role-8 linked fields
   */
  private pullRole9Values(role9Data: Record<string, any>): void {
    // Find all controls with linkTo configuration
    const linkedControls = this.schema.controls.filter(
      (control) => control.ui.linkTo && control.ui.linkTo.role === '210100'
    );

    let inheritedCount = 0;

    // Update each linked field with data from Role-9
    linkedControls.forEach((control) => {
      const sourceQname = control.ui.linkTo!.qname;

      // Update all 4 columns (Consolidated Current, Consolidated Previous, Separate Current, Separate Previous)
      const columns: Array<{
        consolidatedOrSeparate: 'Consolidated' | 'Separate';
        period: 'Current' | 'Previous';
      }> = [
        { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
        { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
        { consolidatedOrSeparate: 'Separate', period: 'Current' },
        { consolidatedOrSeparate: 'Separate', period: 'Previous' },
      ];

      columns.forEach(({ consolidatedOrSeparate, period }) => {
        // Build control name (same format for both Role-9 and Role-8)
        const periodLabel =
          period === 'Current' ? 'CurrentYear' : 'PreviousYear';

        const role9ControlName = `${sourceQname}[${consolidatedOrSeparate}][${periodLabel}]`;
        const role8ControlName = `${control.qname}[${consolidatedOrSeparate}][${periodLabel}]`;

        // Get value from Role-9 data
        const role9Value = role9Data[role9ControlName];

        if (role9Value !== undefined && role9Value !== null) {
          // Update Role-8 form control
          const formControl = this.form.get(role8ControlName);
          if (formControl) {
            formControl.setValue(role9Value, { emitEvent: false });
            // IMPORTANT: Mark as pristine to prevent showing 'Edited' status
            formControl.markAsPristine();
            inheritedCount++;
          } else {
            console.warn(`  ⚠️ Control not found: ${role8ControlName}`);
          }
        }
      });
    });
  }

  /* ============================================================================
     * OLD COMPONENT-BASED CALCULATION LOGIC (COMMENTED OUT)
     * Migrated to CalculationService.calculateTotals_210000()
     * Kept for reference and potential rollback if needed
     * ============================================================================
    // EXISTING LOGIC KEPT BELOW (for comparison and fallback)
    // This will be removed in a future refactoring after testing
    // Get taxonomy-specific qname for a field
    // Maps generic field names to the correct qname based on current taxonomy (MFRS/MPERS)
   
  private getFieldQname(fieldName: string): string {
    const isMpers = this.getXbrlPrefix() === 'ifrs-smes';

    // Map of field names to qnames (MFRS -> MPERS)
    const fieldMap: { [key: string]: { mfrs: string; mpers: string } } = {
      PropertyPlantAndEquipment: {
        mfrs: 'ifrs-full:PropertyPlantAndEquipment',
        mpers: 'ifrs-smes:PropertyPlantAndEquipment',
      },
      InvestmentProperty: {
        mfrs: 'ifrs-full:InvestmentProperty',
        mpers: 'ifrs-smes:InvestmentProperty',
      },
      BiologicalAssets: {
        mfrs: 'ifrs-full:BiologicalAssets',
        mpers: 'ssmt-mpers:NoncurrentBiologicalAssets',
      },
      RightofuseAssets: {
        mfrs: 'ifrs-full:RightofuseAssets',
        mpers: 'ifrs-smes:RightofuseAssets',
      },
      ServiceConcessionAssets: {
        mfrs: 'ifrs-full:ServiceConcessionAssets',
        mpers: 'ssmt-mpers:ServiceConcessionAssets',
      },
      IntangibleAssetsOtherThanGoodwill: {
        mfrs: 'ifrs-full:IntangibleAssetsOtherThanGoodwill',
        mpers: 'ifrs-smes:IntangibleAssetsAndGoodwill',
      },
      InvestmentsInSubsidiaries: {
        mfrs: 'ifrs-full:InvestmentsInSubsidiaries',
        // Intentional typo "Subisidiaries" — must match the SSM MPERS taxonomy element exactly or
        // Arelle stops matching this total. Do NOT "correct" to InvestmentInSubsidiaries (MPERS-only;
        // reverted in c933312). See docs/specifications/sheets/09b_sub-classification-assets-liabilities-equity-mpers/04_investments-subsidiaries.md
        mpers: 'ssmt-mpers:InvestmentInSubisidiaries',
      },
      InvestmentsInAssociates: {
        mfrs: 'ifrs-full:InvestmentsInAssociates',
        mpers: 'ifrs-smes:InvestmentsInAssociates',
      },
      InvestmentsInJointVentures: {
        mfrs: 'ifrs-full:InvestmentsInJointVentures',
        mpers: 'ifrs-smes:InvestmentsInJointVentures',
      },
      NoncurrentInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod: {
        mfrs: 'ifrs-full:NoncurrentInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod',
        mpers:
          'ssmt-mpers:NoncurrentInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod',
      },
      NoncurrentReceivables: {
        mfrs: 'ifrs-full:NoncurrentReceivables',
        mpers: 'ifrs-smes:NoncurrentReceivables',
      },
      ContractAssets: {
        mfrs: 'ifrs-full:ContractAssets',
        mpers: 'ssmt-mpers:NoncurrentContractAssets',
      },
      DeferredTaxAssets: {
        mfrs: 'ifrs-full:DeferredTaxAssets',
        mpers: 'ifrs-smes:DeferredTaxAssets',
      },
      NoncurrentDerivativeFinancialAssets: {
        mfrs: 'ifrs-full:NoncurrentDerivativeFinancialAssets',
        mpers: 'ssmt-mpers:NoncurrentDerivativeFinancialAssets',
      },
      OtherNoncurrentAssets: {
        mfrs: 'ifrs-full:OtherNoncurrentAssets',
        mpers: 'ssmt-mpers:OtherNoncurrentAssets',
      },
      NoncurrentAssets: {
        mfrs: 'ifrs-full:NoncurrentAssets',
        mpers: 'ifrs-smes:NoncurrentAssets',
      },
      Inventories: {
        mfrs: 'ifrs-full:Inventories',
        mpers: 'ifrs-smes:InventoriesTotal',
      },
      CurrentBiologicalAssets: {
        mfrs: 'ifrs-full:CurrentBiologicalAssets',
        mpers: 'ssmt-mpers:CurrentBiologicalAssets',
      },
      CurrentInvestments: {
        mfrs: 'ifrs-full:CurrentInvestments',
        mpers: 'ssmt-mpers:CurrentInvestments',
      },
      CurrentTaxAssetsCurrent: {
        mfrs: 'ifrs-full:CurrentTaxAssetsCurrent',
        mpers: 'ifrs-smes:CurrentTaxAssetsCurrent',
      },
      TradeAndOtherCurrentReceivables: {
        mfrs: 'ifrs-full:TradeAndOtherCurrentReceivables',
        mpers: 'ifrs-smes:TradeAndOtherCurrentReceivables',
      },
      CurrentContractAssets: {
        mfrs: 'ifrs-full:CurrentContractAssets',
        mpers: 'ssmt-mpers:CurrentContractAssets',
      },
      CurrentDerivativeFinancialAssets: {
        mfrs: 'ifrs-full:CurrentDerivativeFinancialAssets',
        mpers: 'ssmt-mpers:CurrentDerivativeFinancialAssets',
      },
      CashAndCashEquivalents: {
        mfrs: 'ifrs-full:CashAndCashEquivalents',
        mpers: 'ssmt:CashAndBankBalances',
      },
      OtherCurrentAssets: {
        mfrs: 'ifrs-full:OtherCurrentAssets',
        mpers: 'ssmt-mpers:OtherCurrentAssets',
      },
      CurrentAssetsOtherThanAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners:
        {
          mfrs: 'ifrs-full:CurrentAssetsOtherThanAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners',
          mpers:
            'ifrs-smes:CurrentAssetsOtherThanAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners',
        },
      NoncurrentAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners:
        {
          mfrs: 'ifrs-full:NoncurrentAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners',
          mpers:
            'ifrs-smes:NoncurrentAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners',
        },
      CurrentAssets: {
        mfrs: 'ifrs-full:CurrentAssets',
        mpers: 'ifrs-smes:CurrentAssets',
      },
      Assets: { mfrs: 'ifrs-full:Assets', mpers: 'ifrs-smes:Assets' },
      CurrentBorrowings: {
        mfrs: 'ifrs-full:CurrentBorrowings',
        mpers: 'ssmt-mpers:CurrentBorrowings',
      },
      CurrentLeaseLiabilities: {
        mfrs: 'ifrs-full:CurrentLeaseLiabilities',
        mpers: 'ssmt-mpers:CurrentLeaseLiabilities',
      },
      CurrentProvisionsForEmployeeBenefits: {
        mfrs: 'ifrs-full:CurrentProvisionsForEmployeeBenefits',
        mpers: 'ifrs-smes:CurrentProvisionsForEmployeeBenefits',
      },
      CurrentProvisions: {
        mfrs: 'ifrs-full:CurrentProvisions',
        mpers: 'ifrs-smes:CurrentProvisions',
      },
      CurrentTaxLiabilitiesCurrent: {
        mfrs: 'ifrs-full:CurrentTaxLiabilitiesCurrent',
        mpers: 'ifrs-smes:CurrentTaxLiabilitiesCurrent',
      },
      TradeAndOtherCurrentPayables: {
        mfrs: 'ifrs-full:TradeAndOtherCurrentPayables',
        mpers: 'ifrs-smes:TradeAndOtherCurrentPayables',
      },
      CurrentContractLiabilities: {
        mfrs: 'ifrs-full:CurrentContractLiabilities',
        mpers: 'ssmt-mpers:CurrentContractLiabilities',
      },
      CurrentDerivativeFinancialLiabilities: {
        mfrs: 'ifrs-full:CurrentDerivativeFinancialLiabilities',
        mpers: 'ssmt-mpers:CurrentDerivativeFinancialLiabilities',
      },
      OtherCurrentLiabilities: {
        mfrs: 'ifrs-full:OtherCurrentLiabilities',
        mpers: 'ssmt-mpers:OtherCurrentLiabilities',
      },
      CurrentLiabilitiesOtherThanLiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale:
        {
          mfrs: 'ifrs-full:CurrentLiabilitiesOtherThanLiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale',
          mpers:
            'ifrs-smes:CurrentLiabilitiesOtherThanLiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale',
        },
      LiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale: {
        mfrs: 'ifrs-full:LiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale',
        mpers:
          'ifrs-smes:LiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale',
      },
      CurrentLiabilities: {
        mfrs: 'ifrs-full:CurrentLiabilities',
        mpers: 'ifrs-smes:CurrentLiabilities',
      },
      NoncurrentServiceConcessionLiabilities: {
        mfrs: 'ifrs-full:NoncurrentServiceConcessionLiabilities',
        mpers: 'ssmt-mpers:NoncurrentServiceConcessionLiabilities',
      },
      NoncurrentBorrowings: {
        mfrs: 'ifrs-full:NoncurrentBorrowings',
        mpers: 'ssmt-mpers:NoncurrentBorrowings',
      },
      NoncurrentLeaseLiabilities: {
        mfrs: 'ifrs-full:NoncurrentLeaseLiabilities',
        mpers: 'ssmt-mpers:NoncurrentLeaseLiabilities',
      },
      NoncurrentProvisionsForEmployeeBenefits: {
        mfrs: 'ifrs-full:NoncurrentProvisionsForEmployeeBenefits',
        mpers: 'ifrs-smes:NoncurrentProvisionsForEmployeeBenefits',
      },
      NoncurrentProvisions: {
        mfrs: 'ifrs-full:NoncurrentProvisions',
        mpers: 'ifrs-smes:NoncurrentProvisions',
      },
      NoncurrentPayables: {
        mfrs: 'ifrs-full:NoncurrentPayables',
        mpers: 'ifrs-smes:NoncurrentPayables',
      },
      NoncurrentContractLiabilities: {
        mfrs: 'ifrs-full:NoncurrentContractLiabilities',
        mpers: 'ssmt-mpers:NoncurrentContractLiabilities',
      },
      DeferredTaxLiabilities: {
        mfrs: 'ifrs-full:DeferredTaxLiabilities',
        mpers: 'ifrs-smes:DeferredTaxLiabilities',
      },
      NoncurrentDerivativeFinancialLiabilities: {
        mfrs: 'ifrs-full:NoncurrentDerivativeFinancialLiabilities',
        mpers: 'ssmt-mpers:NoncurrentDerivativeFinancialLiabilities',
      },
      OtherNoncurrentLiabilities: {
        mfrs: 'ifrs-full:OtherNoncurrentLiabilities',
        mpers: 'ssmt-mpers:OtherNoncurrentLiabilities',
      },
      NoncurrentLiabilities: {
        mfrs: 'ifrs-full:NoncurrentLiabilities',
        mpers: 'ifrs-smes:NoncurrentLiabilities',
      },
      Liabilities: {
        mfrs: 'ifrs-full:Liabilities',
        mpers: 'ifrs-smes:Liabilities',
      },
      IssuedCapital: {
        mfrs: 'ifrs-full:IssuedCapital',
        mpers: 'ifrs-smes:IssuedCapital',
      },
      RetainedEarnings: {
        mfrs: 'ifrs-full:RetainedEarnings',
        mpers: 'ifrs-smes:RetainedEarnings',
      },
      TreasuryShares: {
        mfrs: 'ifrs-full:TreasuryShares',
        mpers: 'ifrs-smes:TreasuryShares',
      },
      OtherReserves: {
        mfrs: 'ifrs-full:OtherReserves',
        mpers: 'ifrs-smes:OtherReserves',
      },
      EquityAttributableToOwnersOfParent: {
        mfrs: 'ifrs-full:EquityAttributableToOwnersOfParent',
        mpers: 'ifrs-smes:EquityAttributableToOwnersOfParent',
      },
      OtherComponentsOfEquity: {
        mfrs: 'ifrs-full:OtherComponentsOfEquity',
        mpers: 'ssmt-mpers:OtherComponentsOfEquity',
      },
      NoncontrollingInterests: {
        mfrs: 'ifrs-full:NoncontrollingInterests',
        mpers: 'ifrs-smes:NoncontrollingInterests',
      },
      Equity: { mfrs: 'ifrs-full:Equity', mpers: 'ifrs-smes:Equity' },
      EquityAndLiabilities: {
        mfrs: 'ifrs-full:EquityAndLiabilities',
        mpers: 'ifrs-smes:EquityAndLiabilities',
      },
    };

    const mapping = fieldMap[fieldName];
    if (!mapping) {
      console.warn(`No qname mapping found for field: ${fieldName}`);
      return fieldName; // Fallback to field name
    }

    return isMpers ? mapping.mpers : mapping.mfrs;
  }

  // END OF EXISTING LOGIC (to be removed after testing)
    ============================================================================ */

  /**
   * Calculate all totals for all 4 columns
   * Now uses CalculationService for centralized calculation logic
   */
  private calculateTotals(): void {
    this.isCalculating = true;

    // NEW: Use CalculationService for centralized calculation logic
    // Get current form data
    const formData = this.form.getRawValue();

    // Get XBRL prefix (ifrs-full for MFRS, ifrs-smes for MPERS)
    const xbrlPrefix = this.getXbrlPrefix() as 'ifrs-full' | 'ifrs-smes';

    // Call calculation service to get calculated totals
    const calculatedTotals = this.calculationService.calculateTotals_210000(
      formData,
      xbrlPrefix
    );

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfFinancialPosition,
      Object.keys(calculatedTotals)
    );

    // Apply calculated totals to form controls
    Object.keys(calculatedTotals).forEach((controlName) => {
      const control = this.form.get(controlName);
      if (control) {
        control.setValue(calculatedTotals[controlName], { emitEvent: false });
        // Mark as pristine to prevent calculated fields from showing 'Edited' status
        control.markAsPristine();
      }
    });

    /* ============================================================================
     * OLD COMPONENT-BASED CALCULATION LOGIC (COMMENTED OUT)
     * Migrated to CalculationService.calculateTotals_210000()
     * Kept for reference and potential rollback if needed
     * ============================================================================
    // EXISTING LOGIC KEPT BELOW (for comparison and fallback)
    // This will be removed in a future refactoring after testing
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];

    columns.forEach(({ consolidatedOrSeparate, period }) => {
      const getValue = (fieldName: string): number => {
        const qname = this.getFieldQname(fieldName);
        const controlName = this.getXbrlControlName(
          qname,
          consolidatedOrSeparate,
          period
        );
        const value = this.form.get(controlName)?.value;
        return value ? parseFloat(value) : 0;
      };

      const setValue = (fieldName: string, value: number): void => {
        const qname = this.getFieldQname(fieldName);
        const controlName = this.getXbrlControlName(
          qname,
          consolidatedOrSeparate,
          period
        );
        const control = this.form.get(controlName);
        if (control) {
          control.setValue(value, { emitEvent: false });
          // Mark as pristine to prevent calculated fields from showing 'Edited' status
          control.markAsPristine();
        }
      };

      // Calculate Total Non-Current Assets
      const totalNonCurrentAssets =
        getValue('PropertyPlantAndEquipment') +
        getValue('InvestmentProperty') +
        getValue('BiologicalAssets') +
        getValue('RightofuseAssets') +
        getValue('ServiceConcessionAssets') +
        getValue('IntangibleAssetsOtherThanGoodwill') +
        getValue('InvestmentsInSubsidiaries') +
        getValue('InvestmentsInAssociates') +
        getValue('InvestmentsInJointVentures') +
        getValue(
          'NoncurrentInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod'
        ) +
        getValue('NoncurrentReceivables') +
        getValue('ContractAssets') +
        getValue('DeferredTaxAssets') +
        getValue('NoncurrentDerivativeFinancialAssets') +
        getValue('OtherNoncurrentAssets');
      setValue('NoncurrentAssets', totalNonCurrentAssets);

      // Calculate Total Current Assets (other than assets held for sale)
      const totalCurrentAssetsOtherThanHeldForSale =
        getValue('Inventories') +
        getValue('CurrentBiologicalAssets') +
        getValue('CurrentInvestments') +
        getValue('CurrentTaxAssetsCurrent') +
        getValue('TradeAndOtherCurrentReceivables') +
        getValue('CurrentContractAssets') +
        getValue('CurrentDerivativeFinancialAssets') +
        getValue('CashAndCashEquivalents') +
        getValue('OtherCurrentAssets');
      setValue(
        'CurrentAssetsOtherThanAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners',
        totalCurrentAssetsOtherThanHeldForSale
      );

      // Calculate Total Current Assets (including held for sale)
      const totalCurrentAssets =
        totalCurrentAssetsOtherThanHeldForSale +
        getValue(
          'NoncurrentAssetsOrDisposalGroupsClassifiedAsHeldForSaleOrAsHeldForDistributionToOwners'
        );
      setValue('CurrentAssets', totalCurrentAssets);

      // Calculate Total Assets
      const totalAssets = totalNonCurrentAssets + totalCurrentAssets;
      setValue('Assets', totalAssets);

      // Calculate Total Current Liabilities (other than liabilities held for sale)
      const totalCurrentLiabilitiesOtherThanHeldForSale =
        getValue('CurrentBorrowings') +
        getValue('CurrentLeaseLiabilities') +
        getValue('CurrentProvisionsForEmployeeBenefits') +
        getValue('CurrentProvisions') +
        getValue('CurrentTaxLiabilitiesCurrent') +
        getValue('TradeAndOtherCurrentPayables') +
        getValue('CurrentContractLiabilities') +
        getValue('CurrentDerivativeFinancialLiabilities') +
        getValue('OtherCurrentLiabilities');
      setValue(
        'CurrentLiabilitiesOtherThanLiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale',
        totalCurrentLiabilitiesOtherThanHeldForSale
      );

      // Calculate Total Current Liabilities (including held for sale)
      const totalCurrentLiabilities =
        totalCurrentLiabilitiesOtherThanHeldForSale +
        getValue('LiabilitiesIncludedInDisposalGroupsClassifiedAsHeldForSale');
      setValue('CurrentLiabilities', totalCurrentLiabilities);

      // Calculate Total Non-Current Liabilities
      const totalNonCurrentLiabilities =
        getValue('NoncurrentServiceConcessionLiabilities') +
        getValue('NoncurrentBorrowings') +
        getValue('NoncurrentLeaseLiabilities') +
        getValue('NoncurrentProvisionsForEmployeeBenefits') +
        getValue('NoncurrentProvisions') +
        getValue('NoncurrentPayables') +
        getValue('NoncurrentContractLiabilities') +
        getValue('DeferredTaxLiabilities') +
        getValue('NoncurrentDerivativeFinancialLiabilities') +
        getValue('OtherNoncurrentLiabilities');
      setValue('NoncurrentLiabilities', totalNonCurrentLiabilities);

      // Calculate Total Liabilities
      const totalLiabilities =
        totalCurrentLiabilities + totalNonCurrentLiabilities;
      setValue('Liabilities', totalLiabilities);

      // Calculate Total Equity Attributable to Owners of Parent
      // Note: Treasury shares is the only debit field, so we subtract it
      const totalEquityAttributable =
        getValue('IssuedCapital') +
        getValue('RetainedEarnings') -
        getValue('TreasuryShares') + // Subtract Treasury shares (debit balance)
        getValue('OtherReserves');
      setValue('EquityAttributableToOwnersOfParent', totalEquityAttributable);

      // Calculate Total Equity
      // Total Equity = Total equity attributable to owners + Equity - other components + Non-controlling interests
      const totalEquity =
        totalEquityAttributable +
        getValue('OtherComponentsOfEquity') +
        getValue('NoncontrollingInterests');
      setValue('Equity', totalEquity);

      // Calculate Total Equity and Liabilities
      const totalEquityAndLiabilities = totalEquity + totalLiabilities;
      setValue('EquityAndLiabilities', totalEquityAndLiabilities);
    });
    // END OF EXISTING LOGIC (to be removed after testing)
    ============================================================================ */

    this.isCalculating = false;
  }

  /**
   * Validate calculated totals against baseline data
   * Only validates calculated/readonly fields (Totals)
   */
  private validateAgainstApiData(): void {
    this.mandatoryFieldErrors.clear();
    this.validateMandatoryFields(); // preserve existing pre-step

    const columns = [
      { key: 'Consolidated|Current', label: 'Consolidated Current' },
      { key: 'Consolidated|Previous', label: 'Consolidated Previous' },
      { key: 'Separate|Current', label: 'Separate Current' },
      { key: 'Separate|Previous', label: 'Separate Previous' },
    ];

    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfFinancialPosition,
      controls: this.schema.controls,
      columns,
      getControlName: (qname, key) => {
        const [cs, period] = key.split('|') as ['Consolidated' | 'Separate', 'Current' | 'Previous'];
        return this.getXbrlControlName(qname, cs, period);
      },
      getCurrentValue: (name) => this.form.get(name)?.value,
    });

    this.extractedSumWarning.mergeIntoValidationErrors(
      this.validationErrors,
      this.schema.controls,
      warnings
    );

    this.updateStatementSheetRowStatusInStateService();
  }

  /**
   * Validate mandatory fields - checks if any field with required=true in schema has null value
   * 0 is acceptable, only null triggers the error "This field is mandatory."
   */
  private validateMandatoryFields(): void {
    // Get all schema controls that have required: true
    // `isMandatoryForValidation` — NOT `isControlMandatory`. It excludes
    // computed controls, so the 8 calculated totals the PO's list flags on this
    // sheet light their `*` without becoming submit blocks. Same predicate the
    // pure validator's `checkRequiredFieldErrors` uses, which is what keeps the
    // editor and the headless verdict from diverging here. See the Developer
    // Notes on that function.
    const requiredControls = this.schema.controls.filter(
      (control) => isMandatoryForValidation(control) && this.isMonetaryItem(control)
    );

    // Define columns to validate based on visibility
    const columnsToValidate: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'Current',
        label: 'Consolidated Current',
        show: this.showConsolidatedCurrent,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'Previous',
        label: 'Consolidated Previous',
        show: this.showConsolidatedPrevious,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'Current',
        label: 'Separate Current',
        show: this.showSeparateCurrent,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'Previous',
        label: 'Separate Previous',
        show: this.showSeparatePrevious,
      },
    ];

    // Count visible columns to determine error message format
    requiredControls.forEach((control) => {
      const errors: string[] = [];

      columnsToValidate.forEach(
        ({ consolidatedOrSeparate, period, label, show }) => {
          if (!show) return;

          const controlName = this.getXbrlControlName(
            control.qname,
            consolidatedOrSeparate,
            period
          );
          const formControl = this.form.get(controlName);

          // Check if value is null (0 is acceptable)
          if (formControl && formControl.value === null) {
            const errorMessage = `${label}: This field is mandatory`
            errors.push(errorMessage);
            this.mandatoryFieldErrors.add(control.qname);
          }
        }
      );

      if (errors.length > 0) {
        const existingErrors = this.validationErrors.get(control.qname) || [];
        this.validationErrors.set(control.qname, [...existingErrors, ...errors]);
      }
    });
  }

  /**
   * Update statement sheet row status in state service
   * Called after validation or edit mode changes to update the dropdown indicator
   * - hasFailedRows: true if any row has 'Failed' Mapping Status
   * - hasEditedRows: true if any row has been edited/updated
   */
  private updateStatementSheetRowStatusInStateService(skipSheetLevelStatus: boolean = false): void {
    // Check if any row has failed validation
    // Failed means: mandatory field errors OR equality validation errors OR form control errors
    const hasFailedRows =
      this.mandatoryFieldErrors.size > 0 ||
      this.equalityValidationErrors.size > 0;

    // Check if any row has been edited
    const hasEditedRows = (this.schema?.controls ?? []).some(
      (control) => this.isMonetaryItem(control) && this.getRowValidationStatus(control) === 'Edited'
    );

    // Update state service
    this.stateService.updateStatementSheetRowStatus(SHEET_KEYS.statementOfFinancialPosition, {
      hasFailedRows,
      hasEditedRows,
    });

    // Also report sheet-level status for dropdown indicator
    // Skip during initial load (before validation runs) to avoid overwriting with empty error state
    if (!skipSheetLevelStatus) {
    }
  }

  /**
   * Load saved state from state service
   * On page refresh, loads original API data
   * On subsequent navigation, loads saved/modified state
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfFinancialPosition);

    // Refresh-discard is now enforced centrally by FilingFormStateService:
    // on the first setConversionId() call of each JS context the modified
    // cache is reset to the original (backend) cache, so getSheetData() here
    // already returns pristine backend values after a refresh. The previous
    // role-local "load from validationBaseline on reload" branch has been
    // removed to eliminate duplication and prevent the two code paths from
    // drifting apart.

    // Load saved state from FilingFormStateService
    const savedData = this.stateService.getSheetData(
      SHEET_KEYS.statementOfFinancialPosition
    );

    if (savedData && Object.keys(savedData).length > 0) {
      this.form.patchValue(savedData, { emitEvent: false });

      // IMPORTANT: Mark form as pristine after loading to prevent showing 'Edited' status on initial load
      this.form.markAsPristine();

      setTimeout(async () => {
        // Re-enter hydration window: this deferred init write fires AFTER
        // ngOnInit's outer begin/endHydration bracket has closed. Without
        // this, the updateSheetData below would emit a [notify-entry] for
        // 'modifiedState' even though no user action occurred.
        this.stateService.beginHydration();
        try {
          this.calculateTotals();

          // Save calculated values to state so other sheets (e.g., Role 9 sub-classification)
          // read correct values for cross-sheet validation even if user navigates immediately
          const fullFormData = this.form.getRawValue();
          this.stateService.updateSheetData(SHEET_KEYS.statementOfFinancialPosition, fullFormData, this.form.valid, true);

          // IMPORTANT: await runAllValidations so the async cross-sheet equity
          // validation (role-610000 SOCIE schema load + validateCrossSheetEquityByYear)
          // has written its errors before evaluateSheetStatus() reads them.
          // Previously fire-and-forget, which caused the sheet to report
          // hasErrors=false before cross-sheet errors were computed, producing
          // wrong cached icons that survived refreshes.
          await this.runAllValidations();
          this.form.markAsPristine();
          this.isLoadingState = false;
          this.isValidationComplete = true;
        } finally {
          this.stateService.endHydration();
        }

        // Snapshot baseline after debounced valueChanges settle, then mark initialized
        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfFinancialPosition);
        }, 600);
      }, 100);
    } else {
      setTimeout(() => {
        this.calculateTotals();
        this.isLoadingState = false;
        this.isValidationComplete = true;

        setTimeout(() => {
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfFinancialPosition);
        }, 600);
      }, 100);
    }
  }

  // setupStatePersistence() removed — collapsed into setupEditPipeline() (single persist).

  /**
   * Cross-tab remote-update re-validation trigger.
   *
   * Why this exists:
   *   runEqualityValidations() (called by runAllValidations()) performs
   *   validateCrossSheetEquityByYear against the SOCIE (role-610000) schema +
   *   the current global state. When a sibling tab edits SOCIE (or any sheet
   *   feeding into the cross-sheet equity reconciliation), the rehydrated
   *   modifiedState lands in this tab's BehaviorSubject — but role8's existing
   *   form.valueChanges subscription does NOT fire (form isn't patched on
   *   remote rehydrate, and it shouldn't be — the user might be on a
   *   different sheet). Without this listener, role8's red borders / row
   *   statuses would stay stale until the user re-entered edit mode.
   *
   * Trigger: any 'modifiedState' rehydrate from applyRemoteChange().
   * We don't filter by sheetKey because the source tab may not have included
   * one, and even when it has, the receiving role may depend on multiple
   * upstream slices. runAllValidations() is idempotent and inexpensive.
   *
   * No write loop: runAllValidations() writes sheet-level status via
   * updateSheetLevelStatus() which never calls notifyCrossTab(). The
   * isApplyingRemote guard has already cleared by the time this fires
   * (see filing-form-state.service applyRemoteChange ordering).
   */
  private setupRemoteUpdateRevalidation(): void {
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfFinancialPosition)),
        filter(() => !!this.schema && !!this.form),
        takeUntil(this.destroy$)
      )
      .subscribe(async ({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role8-statementOfFinancialPosition',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.statementOfFinancialPosition))
          this.adoptRemoteState();

        // Always re-run the cross-sheet pass so the equality/SOCIE verdict (and
        // its frozen entries) refreshes. NOTE: this tab's own form is NOT
        // guaranteed untouched at this point — when `changedSheets` includes
        // this sheet's own slice, `adoptRemoteState()` above already repainted
        // it, so the same-sheet entries reflect the freshly-adopted values, not
        // stale ones. Only an other-slice-only update leaves this sheet's own
        // form genuinely untouched. Awaited so the cross-sheet errors land in
        // validationErrors BEFORE evaluateSheetStatus reads them.
        await this.validateCrossSheet();
        this.evaluateSheetStatus();
      });
  }

  /**
   * Cross-tab adoption for role8 (SoFP). Rows currently open in edit mode are
   * skipped — the user's in-flight work wins until they tick (local overwrites
   * and broadcasts) or press X (the row lands on the adopted value; see
   * `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.form,
      this.stateService.getSheetData(SHEET_KEYS.statementOfFinancialPosition),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Check if control is TextBlock (for Section 1)
   */
  shouldDisplayField(control: SchemaControl): boolean {
    return control.type === 'TextBlock';
  }

  /**
   * Check if control is monetary item (for table display)
   */
  isMonetaryItem(control: SchemaControl): boolean {
    return (
      control.type === 'MonetaryItemType' && control.ui.dualColumn === true
    );
  }

  /**
   * Check if control is section header
   */
  isSectionHeader(control: SchemaControl): boolean {
    return control.type === 'SectionHeader';
  }

  /**
   * Whether the section header at `index` is collapsible — renders a chevron
   * and toggles on click. Delegates to the shared, structural
   * `isCollapsibleHeader` rule (any header deeper than the level-1 banner that
   * encloses rows — no level ceiling, so it can't drift as the schema gains
   * deeper headers). Default `minCollapsibleLevel` of 2 keeps the level-1
   * "Statement of financial position" banner non-toggled.
   */
  isCollapsibleHeader(index: number): boolean {
    return !!this.schema && isCollapsibleHeaderRule(
      this.schema.controls,
      index,
      (control) => this.isSectionHeader(control),
    );
  }

  /**
   * Get header level class. Delegates to the shared `headerLevelClass`, which
   * maps a numeric or `'hN'` level 1:1 to `header-level-N` (no level ceiling)
   * and falls back to `header-level-1` for a missing/unrecognised level.
   */
  getHeaderClass(level?: string | number): string {
    return headerLevelClass(level);
  }

  /**
   * Check if field is calculated/readonly
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet cannot drift from
   * the other 16 — the marker used to live only inside
   * `<label class="field-label">`, which this sheet does not render, so its
   * mandatory rows went unmarked from the initial scaffolding until 2026-08-11.
   * See `isControlMandatory` in `required-field.util.ts`.
   *
   * `crossSheetValue` is supplied because a cross-sheet `requiredIf` degrades to
   * "not mandatory" without it; every other declaration form this sheet uses is
   * unconditional and needs no context. Same-sheet `selfData` is deliberately
   * NOT passed: this sheet keys its values per cell
   * (`qname[Dimension][Period]`), not by bare qname, so a same-sheet lookup
   * would silently miss. Add it — with the right key shape — if a same-sheet
   * conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: SchemaControl): boolean {
    return control.ui.calculated === true || control.ui.readonly === true;
  }

  /**
   * Get validation status for a row (checks all 4 columns)
   * Status priority: Failed > Edited > Validated* > Validated
   * Note: There should be NO empty status - every row must show a status
   */
  getRowValidationStatus(
    control: SchemaControl
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    if (!this.isMonetaryItem(control)) return 'Validated';

    if (this.isRowInEditMode(control)) {
      const frozen = this.frozenRowStatus.get(control.qname);
      if (frozen !== undefined) return frozen;
    }

    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Gather current values (preserves the previous item 1–5 logic).
    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const fc = this.form.get(controlName);
      if (fc) {
        currentValues[controlName] = fc.value;
      }
    }

    // hasError + hasSuggestedChangeWarning are computed by the shared helper so
    // the row background colour (getRowColorIntent) and the Mapping Status badge
    // stay in lock-step over the same inputs.
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(
        SHEET_KEYS.statementOfFinancialPosition
      ),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // linkTo rows inherit a read-only value from Role-9 (sub-classification);
      // the user edits them on the origin sheet, never here, so they can never be
      // "Edited" on Role 8. Mark them inherited so the Edited deviation check is
      // skipped (mirrors the Role 12 fix and keeps the two sheets consistent).
      inherited: !!control.ui.linkTo,
      // Calculated/readonly totals are computed client-side (calculateTotals) and
      // are absent from the persisted baseline, so the deviation check would mark
      // them Edited with zero user edits. They are never user-editable
      // (enterEditMode skips them) — mark them derived to skip the Edited branch.
      derived: this.isCalculatedField(control),
    });
  }

  /**
   * Compute the row's hard-error and suggested-change-warning flags.
   *
   * Extracted VERBATIM from getRowValidationStatus so the Mapping Status badge
   * and the row background colour (getRowColorIntent) are derived from the exact
   * same booleans — they can never disagree. `hasError` deliberately includes the
   * cross-sheet (equalityValidationErrors) and mandatory (mandatoryFieldErrors)
   * checks, which is why the row-colour intent can fold the old
   * `row-has-cross-sheet-error` binding into the 'error' (red) branch without
   * losing any red rows.
   */
  private _computeRowErrorFlags(
    control: SchemaControl
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current' },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous' },
      { consolidatedOrSeparate: 'Separate', period: 'Current' },
      { consolidatedOrSeparate: 'Separate', period: 'Previous' },
    ];
    const controlNames = columns.map((column) =>
      this.getXbrlControlName(control.qname, column.consolidatedOrSeparate, column.period)
    );

    // Only a VISIBLE column may paint the row. A column this permutation does not
    // render can never SHOW a message — every message surface on this sheet
    // filters hidden columns out — so counting one here produces a red row with an
    // empty Error Description. That is the UAT defect of 2026-08-26 (role 18's
    // "Cash and cash equivalents at beginning / end of period" rows, opened on a
    // filing with no previous year): `enable()` makes a hidden, permanently-null
    // required cell invalid, and the reveal on row-open then marks it touched.
    //
    // Developer Note (TODO): `hasValue` below is deliberately still read across
    // ALL FOUR columns. Narrowing it to the visible ones is a separate, correct
    // change — a mandatory row whose only value sits in a hidden column IS
    // unsatisfied — but it can only ADD red, so it must not land until this
    // sheet's required-message builder is confirmed to emit a per-visible-column
    // mandatory line for that case. Otherwise it recreates the very defect above,
    // in the other direction. Role 18 DOES narrow both halves — its
    // `validateRequiredFields()` was made the last writer of the per-column
    // mandatory partition on 2026-08-26, so its red is always accompanied by
    // text; that confirmation is what this sheet still owes.
    const columnVisible: boolean[] = [
      this.showConsolidatedCurrent,
      this.showConsolidatedPrevious,
      this.showSeparateCurrent,
      this.showSeparatePrevious,
    ];

    let hasInvalid = false;
    let hasValue = false;
    controlNames.forEach((controlName, index) => {
      const fc = this.form.get(controlName);
      if (fc) {
        if (columnVisible[index] && fc.invalid && (fc.dirty || fc.touched)) hasInvalid = true;
        if (fc.value !== null && fc.value !== undefined && fc.value !== '') hasValue = true;
      }
    });

    const isRequired = control.required === true;
    let hasError =
      hasInvalid ||
      (isRequired && !hasValue) ||
      this.equalityValidationErrors.has(control.qname) ||
      this.mandatoryFieldErrors.has(control.qname);
    if (!hasError && this.validationErrors.has(control.qname)) {
      const errors = this.validationErrors.get(control.qname) || [];
      hasError = errors.some((message) => !message.includes('does not match the extracted sum'));
    }

    // Suggested-Change warning. Inherited (linkTo) rows participate too — e.g.
    // "Inventories" mirrors Role-9's "Total inventories"; when that inherited
    // value differs from THIS sheet's N8N extracted baseline the row must surface
    // "Validated*" (a per-sheet suggested change), exactly like SOPL (Role 12).
    // The previous `&& !control.ui.linkTo` guard suppressed the asterisk for
    // these rows; it is removed here (and at the three other display surfaces:
    // getRowErrorDescription / hasSuggestedChangeCategory / hasRowValidationError).
    let hasSuggestedChangeWarning = false;
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      const allAreSuggestedChanges = allErrors.every((message) =>
        message.includes('does not match the extracted sum')
      );
      hasSuggestedChangeWarning = allAreSuggestedChanges;
    }

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Compute the row background colour intent: 'error' (red) when the row has a
   * hard error, 'warning' (yellow) when it only has a suggested-change warning,
   * 'none' otherwise. Red outranks yellow. Unlike the Mapping Status this does
   * NOT consult edit mode (no forceEdited), so clicking the edit pencil never
   * repaints the row — fixing the bug where entering edit mode dropped the red.
   */
  private _computeRowColorIntent(control: SchemaControl): 'error' | 'warning' | 'none' {
    if (!this.isMonetaryItem(control)) return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);
    if (hasError) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Public accessor for the row colour intent, called directly from the template
   * (this component intentionally uses an uncached style — no rowColorIntentCache).
   */
  getRowColorIntent(control: SchemaControl): 'error' | 'warning' | 'none' {
    return this._computeRowColorIntent(control);
  }

  /**
   * Get validation icon class
   */
  getValidationIcon(
    status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'
  ): string {
    switch (status) {
      case 'Validated':
        return 'pi pi-check-circle';
      case 'Validated*':
        return 'pi pi-check-circle';
      case 'Failed':
        return 'pi pi-times-circle';
      case 'Edited':
        return 'pi pi-pencil';
      default:
        return '';
    }
  }

  /**
   * Get validation icon color
   */
  getValidationIconColor(
    status: 'Failed' | 'Edited' | 'Validated*' | 'Validated'
  ): string {
    switch (status) {
      case 'Validated':
        return 'green';
      case 'Validated*':
        return '#D97706'; // Amber color for warning severity
      case 'Failed':
        return 'red';
      case 'Edited':
        return 'orange';
      default:
        return '';
    }
  }

  /**
   * Get error description for a row (aggregates from all 4 columns)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   * Only shows errors for visible columns based on Nature of Conversion setting
   * Inherited (linkTo) rows show their "* Suggested Change" too, mirroring SOPL.
   */
  getRowErrorDescription(control: SchemaControl): string {
    if (!this.isMonetaryItem(control)) return '';

    const errors: string[] = [];           // Red - Error category (cross-sheet, mandatory, form control)
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (API mismatch)

    // Column visibility filter helper
    const isVisibleColumn = (error: string): boolean => {
      if (error.includes('Consolidated Current') && !this.showConsolidatedCurrent) return false;
      if (error.includes('Consolidated Previous') && !this.showConsolidatedPrevious) return false;
      if (error.includes('Separate Current') && !this.showSeparateCurrent) return false;
      if (error.includes('Separate Previous') && !this.showSeparatePrevious) return false;
      return true;
    };

    // 1. Process validation errors from validationErrors Map
    if (this.validationErrors.has(control.qname)) {
      const allValidationErrors = this.validationErrors.get(control.qname) || [];

      allValidationErrors.forEach((errorMsg) => {
        // Skip if column is not visible
        if (!isVisibleColumn(errorMsg)) return;

        // Categorize based on error message content
        if (errorMsg.includes('does not match the extracted sum')) {
          // API mismatch warning -> Suggested Change (black). Inherited (linkTo)
          // rows surface this too — mirrors SOPL so the user sees WHY the inherited
          // total carries a "Validated*".
          suggestedChanges.push(errorMsg);
        } else {
          // Cross-sheet validation or mandatory field error -> Error (red)
          errors.push(errorMsg);
        }
      });
    }

    // 2. Collect form control validation errors -> Error category
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
      label: string;
      show: boolean;
    }> = [
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'Current',
        label: 'Consolidated Current',
        show: this.showConsolidatedCurrent,
      },
      {
        consolidatedOrSeparate: 'Consolidated',
        period: 'Previous',
        label: 'Consolidated Previous',
        show: this.showConsolidatedPrevious,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'Current',
        label: 'Separate Current',
        show: this.showSeparateCurrent,
      },
      {
        consolidatedOrSeparate: 'Separate',
        period: 'Previous',
        label: 'Separate Previous',
        show: this.showSeparatePrevious,
      },
    ];

    for (const { consolidatedOrSeparate, period, label, show } of columns) {
      if (!show) continue;

      const controlName = this.getXbrlControlName(
        control.qname,
        consolidatedOrSeparate,
        period
      );
      const formControl = this.form.get(controlName);

      if (
        formControl &&
        formControl.invalid &&
        (formControl.dirty || formControl.touched)
      ) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          if (formControl.errors['required'])
            errorMessages.push('Required field');
          if (formControl.errors['min'])
            errorMessages.push(`Min: ${formControl.errors['min'].min}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // 3. Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror validateAgainstApiData's
    // computeMismatchWarnings call exactly (same composite snapshot keys).
    if (this.baselineDebug.enabled()) {
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.statementOfFinancialPosition,
        control as unknown as SummableControl,
        [
          { key: 'Consolidated|Current', label: 'Consolidated Current' },
          { key: 'Consolidated|Previous', label: 'Consolidated Previous' },
          { key: 'Separate|Current', label: 'Separate Current' },
          { key: 'Separate|Previous', label: 'Separate Previous' },
        ],
        (qname, key) => {
          const [cs, period] = key.split('|') as ['Consolidated' | 'Separate', 'Current' | 'Previous'];
          return this.getXbrlControlName(qname, cs, period);
        }
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Check if row has Error category items (for red text styling)
   * Error category includes: cross-sheet validation, mandatory field, form control errors
   */
  hasErrorCategory(control: SchemaControl): boolean {
    // Check equality and mandatory field errors
    if (this.equalityValidationErrors.has(control.qname)) return true;
    if (this.mandatoryFieldErrors.has(control.qname)) return true;

    // Check for form control validation errors
    const columns: Array<{
      consolidatedOrSeparate: 'Consolidated' | 'Separate';
      period: 'Current' | 'Previous';
      show: boolean;
    }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'Current', show: this.showConsolidatedCurrent },
      { consolidatedOrSeparate: 'Consolidated', period: 'Previous', show: this.showConsolidatedPrevious },
      { consolidatedOrSeparate: 'Separate', period: 'Current', show: this.showSeparateCurrent },
      { consolidatedOrSeparate: 'Separate', period: 'Previous', show: this.showSeparatePrevious },
    ];

    for (const { consolidatedOrSeparate, period, show } of columns) {
      if (!show) continue;

      const controlName = this.getXbrlControlName(control.qname, consolidatedOrSeparate, period);
      const formControl = this.form.get(controlName);

      if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has Suggested Change category items (for black text styling)
   * Suggested Change category includes: API mismatch warnings. Inherited (linkTo)
   * rows are included too, mirroring SOPL (their inherited total can mismatch
   * this sheet's N8N baseline just like a locally-calculated total).
   */
  hasSuggestedChangeCategory(control: SchemaControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const allErrors = this.validationErrors.get(control.qname) || [];

    // Check if any error is an API mismatch warning (Suggested Change)
    return allErrors.some((errorMsg) => {
      // Filter by visibility first
      if (errorMsg.includes('Consolidated Current') && !this.showConsolidatedCurrent) return false;
      if (errorMsg.includes('Consolidated Previous') && !this.showConsolidatedPrevious) return false;
      if (errorMsg.includes('Separate Current') && !this.showSeparateCurrent) return false;
      if (errorMsg.includes('Separate Previous') && !this.showSeparatePrevious) return false;

      // Check if it's an API mismatch warning
      return errorMsg.includes('does not match the extracted sum');
    });
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: SchemaControl): boolean {
    return this.getRowValidationStatus(control) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   *
   * Note: this publishes the component's display-derived boolean. For sheets
   * on the pure-verdict allow-list, FilingFormStateService.updateSheetLevelStatus
   * centrally overrides `hasErrors` with the registered pure validator's verdict
   * (single source of truth shared with the headless listing path), so the
   * sheet-selector tick can never disagree with the listing for the same data.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;
    for (const control of this.schema.controls) {
      if (this.getRowValidationStatus(control) === 'Failed') {
        hasErrors = true;
        break;
      }
    }

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.statementOfFinancialPosition, { hasErrors });
  }

  /**
   * Check if row has validation error (for yellow highlighting)
   * Checks validationErrors Map directly instead of status. Inherited (linkTo)
   * rows are highlighted on a suggested-change mismatch too, mirroring SOPL
   * (Role 12) so SOFP's inherited totals are not a silent exception.
   */
  hasRowValidationError(control: SchemaControl): boolean {
    return this.validationErrors.has(control.qname);
  }

  /**
   * Check if row has equality validation error (for red highlighting)
   * Equality validation errors are errors, not warnings
   */
  hasCrossSheetValidationError(control: SchemaControl): boolean {
    return this.equalityValidationErrors.has(control.qname);
  }

  /**
   * Check if row has mandatory field error (for red highlighting)
   * Mandatory field errors occur when required fields have null values
   */
  hasMandatoryFieldError(control: SchemaControl): boolean {
    return this.mandatoryFieldErrors.has(control.qname);
  }

  /**
   * Check if a specific input field (column) has a 'Failed' status error
   * Used for applying red border styling to individual input fields
   * Only returns true for mandatory field errors (null values in required fields)
   * @param control The schema control for the row
   * @param consolidatedOrSeparate 'Consolidated' or 'Separate'
   * @param period 'Current' or 'Previous'
   * @returns true if this specific column has a Failed status error
   */
  hasColumnError(
    control: SchemaControl,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): boolean {
    if (!this.isMonetaryItem(control)) return false;

    // Get the form control for this specific column
    const controlName = this.getXbrlControlName(
      control.qname,
      consolidatedOrSeparate,
      period
    );
    const formControl = this.form.get(controlName);

    if (!formControl) return false;

    // Check if form control is invalid (dirty/touched)
    if (formControl.invalid && (formControl.dirty || formControl.touched)) {
      return true;
    }

    // Check for mandatory field error (null value in required field)
    if (control.required && formControl.value === null) {
      return true;
    }

    // Check for equality validation errors (e.g., cross-sheet Equity mismatch with SOCIE)
    if (this.equalityValidationErrors.has(control.qname)) {
      const errors = this.equalityValidationErrors.get(control.qname) || [];
      const periodLabel = period === 'Current' ? 'Current' : 'Previous';
      const columnIdentifier = `${consolidatedOrSeparate} ${periodLabel}`;
      if (errors.some((err: any) => (err.message || '').includes(columnIdentifier))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get total field count
   */
  get totalFields(): number {
    return (
      this.schema?.controls?.filter((control) => control.type === 'MonetaryItemType')
        .length || 0
    );
  }

  /**
   * Initialize all sections as expanded by default (Golden Standard pattern)
   */
  private initializeExpandedSections(): void {
    if (!this.schema) return;

    // Seed EVERY section header (all levels, incl. the level-1 banner) as
    // expanded by default. Level 1 is seeded — even though it has no toggle —
    // because the shared `isRowVisible` helper consults level-1 ancestors;
    // without seeding it, level-1 would read as collapsed and hide every row
    // under it on load. Seeding by `isSectionHeader` (not a level allow-list)
    // keeps this from drifting as the schema gains levels.
    this.schema.controls.forEach((control: SchemaControl) => {
      if (this.isSectionHeader(control)) {
        this.expandedSections.add(control.qname);
      }
    });
  }

  /**
   * Toggle section expansion (Golden Standard pattern)
   */
  toggleSection(qname: string): void {
    if (this.expandedSections.has(qname)) {
      this.expandedSections.delete(qname);
    } else {
      this.expandedSections.add(qname);
    }
  }

  /**
   * Check if section is expanded (Golden Standard pattern)
   */
  isSectionExpanded(qname: string): boolean {
    return this.expandedSections.has(qname);
  }

  /**
   * Check if a row should be shown based on parent section expansion.
   *
   * Delegates to the shared, pure `isRowVisible` helper (monotonic
   * strictly-decreasing ancestor walk). Replaces the prior per-level hand-rolled
   * walk that recorded the first header at each level regardless of nesting and
   * so wrongly hid rows whenever a deeper section preceded a shallower one. See
   * `section-visibility.util.ts`. Level-1 roots are seeded as expanded in
   * `initializeExpandedSections`, so they stay visible (they have no toggle).
   */
  shouldShowRow(control: SchemaControl, index: number): boolean {
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpanded(qname),
      (control) => this.isSectionHeader(control)
    );
  }

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: SchemaControl): boolean {
    return this.editingRows.has(control.qname);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls, and mark as editing
   */
  enterEditMode(control: SchemaControl): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(control.qname, this.getRowValidationStatus(control));

    // Store original values for all 4 controls
    const originalValues: { [key: string]: number | null } = {};
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    controlNames.forEach((name) => {
      const formControl = this.form.get(name);
      if (formControl) {
        originalValues[name] = formControl.value ?? null;
      }
    });

    // Enable all 4 controls, revealing only the columns this permutation SHOWS —
    // a hidden required column is permanently null, so enabling makes it invalid
    // and revealing it would paint the row red with no text to explain it.
    enableDualColumnRowAndRevealVisible(
      this.form,
      control.qname,
      this.dimensionVisibility,
      { emitEvent: true }
    );

    this.editingRows.set(control.qname, originalValues);

    // Mark the sheet as actively editing so cross-tab / headless consumers know
    // an uncommitted edit is in progress until the Tick (saveEditMode) clears it.
    this.stateService.markSheetEditing(SHEET_KEYS.statementOfFinancialPosition);
  }

  /**
   * Handle cancel confirmed from submit-for-validation
   * Reloads the saved state and marks the form as pristine
   */
  onCancelConfirmed(): void {
    // Reset validation complete flag to prevent flash during re-validation
    this.isValidationComplete = false;

    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfFinancialPosition);

    // Reload from the reverted state service data
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.form.markAsPristine();
  }

  /**
   * Save edit mode - validate, save to localStorage, disable controls, and exit edit mode
   * If values match the initial API data (validation baseline), the row is not marked as "Edited"
   */
  async saveEditMode(control: SchemaControl): Promise<void> {
    if (!this.editingRows.has(control.qname)) return;

    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    const hasErrors = controlNames.some((name) => {
      const fc = this.form.get(name);
      return fc && fc.invalid;
    });
    if (hasErrors) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix validation errors before saving',
      });
      return;
    }

    // Disable the row's controls and exit edit mode FIRST so the subset we
    // persist reflects the committed (raw) values.
    controlNames.forEach((name) => {
      const fc = this.form.get(name);
      if (fc) fc.disable({ emitEvent: false });
    });
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Commit-time validation sequence: recompute totals FIRST so the persisted
    // subset includes the freshly recalculated calculated/derived totals (the
    // downstream sheets read these), then persist, then refresh verdicts.
    this.calculateTotals();

    // Subset persist: write this row's committed cells PLUS the recalculated
    // calculated totals. updateSheetData's shallow merge leaves every other
    // (possibly mid-edit elsewhere) field at its committed value — this is the
    // commit-on-Tick boundary.
    const subset = pickSubset(this.form.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.stateService.updateSheetData(SHEET_KEYS.statementOfFinancialPosition, subset);

    // "Edited" is now derived from the last-submission baseline (MappingStatusService) —
    // no marker bookkeeping here.
    this.updateStatementSheetRowStatusInStateService();

    // Refresh the same-sheet verdict, then run the cross-sheet (equality / SOCIE)
    // pass — the latter only happens here, never live.
    this.validateSameSheet();
    await this.validateCrossSheet();

    // Reconcile the edited flag against the committed baseline (a revert-to-original
    // + Tick clears the pencil), then release the editing lock and re-publish status.
    this.stateService.reconcileEditedFlag(SHEET_KEYS.statementOfFinancialPosition);
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfFinancialPosition);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - revert to original values if not previously saved, otherwise keep current values
   * - If user didn't trigger saveEditMode previously, revert to the initial values stored when entering edit mode
   * - If user triggered saveEditMode before, keep the changed values
   */
  cancelEditMode(control: SchemaControl): void {
    if (!this.editingRows.has(control.qname)) return;

    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous'),
    ];

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const entryValues = this.editingRows.get(control.qname);
    const adopted = this.stateService.getSheetData(SHEET_KEYS.statementOfFinancialPosition);
    controlNames.forEach((controlName) => {
      const fc = this.form.get(controlName);
      if (fc) {
        const revertValue = controlName in adopted
          ? adopted[controlName]
          : (entryValues?.[controlName] ?? null);
        fc.setValue(revertValue, { emitEvent: false });
        fc.markAsPristine();
        fc.disable();
      }
    });

    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);
    this.calculateTotals();
    // Cancel discards uncommitted typing — only the same-sheet verdict can change
    // (totals reverted). The cross-sheet verdict is unchanged since nothing
    // committed, so re-run the same-sheet pass only.
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfFinancialPosition);
    this.evaluateSheetStatus();
  }

  /**
   * Run JSON-driven equality validation
   * Uses EqualityValidationService to validate equality rules defined in form JSON.
   *
   * Returns a Promise that resolves ONLY after the async cross-sheet equity
   * schema load + validation has completed. Callers MUST await this to avoid
   * reporting sheet-level status before cross-sheet errors have been written.
   */
  private runEqualityValidations(): Promise<void> {
    if (!this.schema) {
      return Promise.resolve();
    }

    // Increment generation to invalidate any pending async callbacks from previous runs
    const currentGeneration = ++this.equalityValidationGeneration;

    // Detect taxonomy (MFRS or MPERS)
    const isMFRS = this.schema.controls.some(
      (control) => control.qname === 'ifrs-full:Assets' || control.qname === 'ifrs-full:EquityAndLiabilities'
    );
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Get data from all sheets via state service (for cross-sheet validation)
    const allSheetData = this.stateService.exportAllData();

    // Cross-sheet recompute parity: before comparing this sheet's
    // values against OTHER sheets, recompute those referenced sheets' totals so
    // the comparison reads fresh (not stale-extracted) operands — matching what
    // the user would see if they opened the referenced sheet. The SAME
    // recomputed bag is reused by validateAllEqualityRules AND the cross-sheet
    // equity reconciliation below (recompute once, reuse). SOCIE is recomputed
    // onto this bag inside the .then() below, once its schema has loaded.
    const referenced = referencedSheetKeysFromSchema(this.schema, SHEET_KEYS.statementOfFinancialPosition);
    // Include the CURRENT sheet (SFP) in the recompute set so its OWN sum-derived
    // totals (Assets / EquityAndLiabilities / Equity) are baseline-anchored for the
    // blocking SAME-SHEET equality (Assets == EquityAndLiabilities) — mirroring the
    // pure validator's `blockingSnapshot` and role 12's recompute set. Otherwise a
    // Role-9 sub-classification detail-sum cascade would re-raise a blocking in-sheet
    // error that disagrees with the dropdown's non-blocking `*` (global rule).
    const recomputeKeys = [...referenced, SHEET_KEYS.statementOfFinancialPosition];
    const formValuesWithAllSheets = recomputeReferencedSheets(
      { ...allSheetData, [SHEET_KEYS.statementOfFinancialPosition]: this.form.getRawValue() },
      recomputeKeys,
      currentTaxonomy,
      // Referenced + own operands are recompute-of-latest — never the N8N
      // snapshot (revised global rule); matches the pure validator.
      (key) =>
        key === SHEET_KEYS.statementOfFinancialPosition
          ? this.schema
          : this.validationContext.getCachedSchema(this.validationContext.resolveSheetKey(key) ?? key),
    );

    // Pass 'statementOfFinancialPosition' as currentSheetKey
    const errors = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfFinancialPosition
    );

    // Run cross-sheet equity by year validation.
    // Returned from the method so callers (runAllValidations) can await the
    // full async chain and sequence evaluateSheetStatus() afterwards.
    const socieSheetKey = '610000';
    return this.formSchemaService.loadSchema(socieSheetKey, currentTaxonomy).then((socieSchema: any) => {
      // Skip if a newer runEqualityValidations call has started
      if (currentGeneration !== this.equalityValidationGeneration) return;

      const mergedErrors = { ...errors };

      if (socieSchema) {
        // Recompute SOCIE onto the shared bag using its now-loaded schema, so
        // the equity reconciliation compares against fresh (recomputed) SOCIE
        // totals — not the stale persisted extraction. The SOCIE column context
        // mirrors the SOCIE editor's own visibility derivation.
        const socieColumnContext = buildSocieColumnContext(
          this.crossRefService.isConsolidated(),
          this.crossRefService.is1stTimePreparation(),
          socieSchema?.equityColumns
        );
        const reconciliationBag = recomputeReferencedSheets(
          formValuesWithAllSheets,
          [SHEET_KEYS.statementOfChangesInEquity],
          currentTaxonomy,
          (key) =>
            this.validationContext.resolveSheetKey(key) === SHEET_KEYS.statementOfChangesInEquity ||
            key === SHEET_KEYS.statementOfChangesInEquity
              ? socieSchema
              : this.validationContext.getCachedSchema(this.validationContext.resolveSheetKey(key) ?? key),
          socieColumnContext
        );

        const crossSheetErrors = this.equityReconciliation.validateCrossSheetEquityByYear(
          { sfp: this.schema, socie: socieSchema },
          reconciliationBag,
          currentTaxonomy
        );

        // Merge cross-sheet errors with standard equality errors. The
        // reconciliation now covers both Current Year (ungated) and Previous Year
        // (consecutive-gated) — see EquityReconciliationService.validateCrossSheetEquityByYear.
        Object.keys(crossSheetErrors).forEach(qname => {
          if (mergedErrors[qname]) {
            mergedErrors[qname] = [...mergedErrors[qname], ...crossSheetErrors[qname]];
          } else {
            mergedErrors[qname] = crossSheetErrors[qname];
          }
        });
      }

      // Clean up stale equality errors from validationErrors for qnames that no longer have errors
      const previousEqualityQnames = new Set(this.equalityValidationErrors.keys());

      // Store merged errors
      this.equalityValidationErrors = new Map(Object.entries(mergedErrors));

      // Remove resolved equality errors from validationErrors
      previousEqualityQnames.forEach(qname => {
        if (!mergedErrors[qname]) {
          const existingErrors = this.validationErrors.get(qname) || [];
          const apiMismatchWarnings = existingErrors.filter(msg =>
            msg.includes(EXTRACTED_SUM_MARKER)
          );
          if (apiMismatchWarnings.length > 0) {
            this.validationErrors.set(qname, apiMismatchWarnings);
          } else {
            this.validationErrors.delete(qname);
          }
        }
      });

      // Add current equality errors to validationErrors (preserve API mismatch warnings)
      Object.keys(mergedErrors).forEach(qname => {
        const errorMessages = mergedErrors[qname].map((err: any) => err.message);
        const existingErrors = this.validationErrors.get(qname) || [];
        const apiMismatchWarnings = existingErrors.filter(msg =>
          msg.includes(EXTRACTED_SUM_MARKER)
        );
        this.validationErrors.set(qname, [...apiMismatchWarnings, ...errorMessages]);
      });

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('✅ Equality validation completed (role8):', {
      //   errorsFound: this.equalityValidationErrors.size,
      //   errors: Object.fromEntries(this.equalityValidationErrors)
      // });
    }).catch((err: any) => {
      console.error('Error loading SOCIE schema:', err);

      // Store standard errors only
      this.equalityValidationErrors = new Map(Object.entries(errors));

      // Also add to validationErrors Map for display compatibility
      Object.keys(errors).forEach(qname => {
        const errorMessages = errors[qname].map((err: any) => err.message);
        const existingErrors = this.validationErrors.get(qname) || [];
        this.validationErrors.set(qname, [...existingErrors, ...errorMessages]);
      });

    });
  }

  /**
   * Check if a field has equality validation error
   */
  hasEqualityError(qname: string): boolean {
    return this.equalityValidationErrors.has(qname);
  }

  /**
   * Get equality validation error message for a field
   */
  getEqualityErrorMessage(qname: string): string {
    const errors = this.equalityValidationErrors.get(qname);
    if (!errors || errors.length === 0) return '';
    return errors.map(err => err.message).join('\n');
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: SchemaControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
